/* ==========================================================================
   px-pdp.js - Ikimsoft PDP Boost runtime (template: product.boost)

   Loaded with defer from sections/px-main-product.liquid, AFTER pubsub.js,
   constants.js and global.js (head, deferred) and after product-info.js and
   product-form.js (same section, deferred, earlier in document order).

   RULES
   - Vanilla ES2020, no dependencies, no frameworks.
   - Every module no-ops when its markup is absent.
   - No timer faster than one tick per minute, except user-driven UI.
   - Never fabricate urgency: the countdown reads a server-provided end time,
     never computes one, and removes itself at zero.
   - Analytics go through Shopify.analytics.publish('ikimsoft:<name>', data);
     the merchant's custom pixel forwards them (build prompt, section 15).
   ========================================================================== */
(() => {
  'use strict';

  /* == 00 UTILITIES ======================================================= */
  const PX = (window.PX = window.PX || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const DEBUG = /[?&]px_debug=1/.test(window.location.search);

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  PX.config = (() => {
    const el = qs('script[data-px-config]');
    try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; }
  })();

  PX.track = (name, data = {}) => {
    const payload = {
      product_id: PX.config.productId || null,
      variant_id: PX.config.variantId || null,
      template: 'product.boost',
      ...data,
    };
    try {
      window.Shopify?.analytics?.publish?.(`ikimsoft:${name}`, payload);
    } catch (e) { /* analytics must never break the page */ }
    if (DEBUG) console.info('[px]', name, payload);
  };

  /* Dawn pub/sub (assets/pubsub.js). PUB_SUB_EVENTS is a top-level const in
     constants.js, so it is NOT a window property: use the literal name. */
  PX.onVariantChange = (cb) =>
    typeof window.subscribe === 'function' ? window.subscribe('variant-change', cb) : () => {};

  /* == 01 HEADER OFFSET + STICKY BUY BOX ================================== */
  /* The site header (.ik-head2 on Ikimsoft; .hm-header elsewhere) is sticky. Measure where it really ends so
     the buy box, anchor jumps and the story nav stop below it.
     Tall buy box: a plain `top: header` would hide its lower half, so set
     top = min(header + 24, viewport - box - 24). For a tall box that value is
     negative: the box scrolls until its bottom is visible, then sticks. */
  (() => {
    const root = document.documentElement;
    const col = qs('[data-px-sticky-col]');
    let ticking = false;

    const headerBottom = () => {
      const h = qs('.hm-header, .ik-head2');
      if (!h) return 0;
      const r = h.getBoundingClientRect();
      return r.top <= 1 && r.bottom > 0 ? Math.round(r.bottom) : 0;
    };

    const update = () => {
      ticking = false;
      const header = headerBottom();
      root.style.setProperty('--px-header-offset', `${header}px`);
      if (!col) return;
      const room = window.innerHeight - col.offsetHeight - 24;
      col.style.setProperty('--px-sticky-top', `${Math.min(header + 24, room)}px`);
    };
    const request = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    };

    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    if (col && 'ResizeObserver' in window) new ResizeObserver(request).observe(col);
  })();

  /* == 02 GALLERY ========================================================= */
  class PxGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-px-track]', this);
      if (!this.track || this.dataset.pxReady) return;
      this.dataset.pxReady = '1';
      this.items = qsa('.px-gallery__item', this.track);
      this.counter = qs('[data-px-counter]', this);
      this.lightbox = qs('[data-px-lightbox]', this);
      this.seen = new Set([0]);
      this.index = 0;

      if ('IntersectionObserver' in window && this.items.length > 1) {
        this.io = new IntersectionObserver(
          (entries) => entries.forEach((e) => {
            if (e.isIntersecting && e.intersectionRatio >= 0.6) this.setIndex(Number(e.target.dataset.index));
          }),
          { root: this.track, threshold: [0.6] }
        );
        this.items.forEach((li) => this.io.observe(li));
      }

      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-px-zoom]');
        if (zoom && !zoom.disabled && this.lightbox) { this.openLightbox(Number(zoom.dataset.pxZoom)); return; }
        const play = e.target.closest('[data-px-video-play]');
        if (play) this.playVideo(play.closest('[data-px-video]'));
      });

      this.track.addEventListener('keydown', (e) => {
        if (desktop.matches || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
        e.preventDefault();
        this.goTo(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      });

      if (this.dataset.selected) this.goToMedia(this.dataset.selected, true);
      this.unsub = PX.onVariantChange((evt) => {
        const id = evt?.data?.variant?.featured_media?.id;
        if (id) this.goToMedia(String(id));
      });

      if (this.lightbox) this.initLightbox();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.unsub?.();
    }

    setIndex(i) {
      this.index = i;
      this.style.setProperty('--px-index', i);
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.items.length}`;
      if (!this.seen.has(i)) { this.seen.add(i); PX.track('gallery_view', { index: i }); }
    }

    goTo(i, instant = false) {
      if (!this.items.length) return;
      const idx = Math.max(0, Math.min(this.items.length - 1, i));
      const li = this.items[idx];
      const how = instant ? 'auto' : behavior();
      if (desktop.matches) li.scrollIntoView({ behavior: how, block: 'nearest' });
      else this.track.scrollTo({ left: li.offsetLeft - this.track.offsetLeft, behavior: how });
      this.setIndex(idx);
    }

    goToMedia(mediaId, instant = false) {
      const li = qs(`[data-media-id="${mediaId}"]`, this.track);
      if (li) this.goTo(Number(li.dataset.index), instant);
    }

    playVideo(wrap) {
      if (!wrap || wrap.dataset.pxPlaying) return;
      const tpl = qs('template[data-px-video-template]', wrap);
      if (!tpl) return;
      wrap.dataset.pxPlaying = '1';
      wrap.replaceChildren(tpl.content.cloneNode(true));
      qs('video', wrap)?.play()?.catch(() => {});
      PX.track('video_play');
    }

    initLightbox() {
      const dlg = this.lightbox;
      const track = qs('[data-px-lb-track]', dlg);
      const count = qs('[data-px-lb-count]', dlg);
      const zoomables = qsa('[data-px-zoom]', this.track);
      let current = 0;

      const label = () => { if (count) count.textContent = `${current + 1} / ${zoomables.length}`; };
      const show = (i) => {
        current = Math.max(0, Math.min(zoomables.length - 1, i));
        track.scrollTo({ left: current * track.clientWidth, behavior: 'auto' });
        label();
      };
      const build = () => {
        if (track.childElementCount) return;
        zoomables.forEach((btn) => {
          const thumb = qs('img', btn);
          const fig = document.createElement('figure');
          fig.className = 'px-lightbox__slide';
          const img = document.createElement('img');
          img.alt = thumb ? thumb.alt : '';
          img.decoding = 'async';
          img.loading = 'lazy';
          img.src = btn.dataset.zoomSrc || thumb?.currentSrc || '';
          fig.appendChild(img);
          track.appendChild(fig);
        });
        track.addEventListener('scroll', () => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== current) { current = i; label(); }
        }, { passive: true });
      };

      this.openLightbox = (mediaIndex) => {
        build();
        const i = zoomables.findIndex((b) => Number(b.dataset.pxZoom) === mediaIndex);
        dlg.showModal();
        document.documentElement.classList.add('px-lb-open');
        requestAnimationFrame(() => show(i < 0 ? 0 : i));
        PX.track('lightbox_open', { index: mediaIndex });
      };

      qs('[data-px-lb-close]', dlg).addEventListener('click', () => dlg.close());
      qs('[data-px-lb-prev]', dlg).addEventListener('click', () => show(current - 1));
      qs('[data-px-lb-next]', dlg).addEventListener('click', () => show(current + 1));
      dlg.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); show(current + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); show(current - 1); }
      });
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
      dlg.addEventListener('close', () => {
        document.documentElement.classList.remove('px-lb-open');
        zoomables[current]?.focus({ preventScroll: true });
      });
    }
  }
  define('px-gallery', PxGallery);

  /* == 03 OFFER COUNTDOWN ================================================= */
  /* Reads data-end-ms from the server. The live count shows only inside the
     final window (data-window-ms), at minute resolution. At zero the bar
     removes itself. The absolute end is re-rendered in the shopper's own time
     zone ("Ends Fri, Oct 31, 11:59 PM CDT"). */
  class PxOffer extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.endMs);
      this.windowMs = Number(this.dataset.windowMs) || 72 * 3600000;
      if (!Number.isFinite(this.end) || this.end <= Date.now()) { this.expire(); return; }

      const abs = qs('[data-px-offer-abs]', this);
      if (abs) {
        try {
          abs.textContent = new Intl.DateTimeFormat(undefined, {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
          }).format(new Date(this.end));
          abs.setAttribute('datetime', new Date(this.end).toISOString());
        } catch (e) { /* keep the server-rendered text */ }
      }
      this.count = qs('[data-px-offer-count]', this);
      this.d = qs('[data-px-d]', this);
      this.h = qs('[data-px-h]', this);
      this.m = qs('[data-px-m]', this);
      this.tick = this.tick.bind(this);
      this.onVis = () => { if (!document.hidden) { clearTimeout(this.timer); this.tick(); } };
      document.addEventListener('visibilitychange', this.onVis);
      this.tick();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVis);
    }

    tick() {
      const left = this.end - Date.now();
      if (left <= 0) { this.expire(); return; }
      if (this.count) {
        const inWindow = left <= this.windowMs;
        this.count.hidden = !inWindow;
        if (inWindow && this.d && this.h && this.m) {
          const totalMin = Math.ceil(left / 60000); /* round up: never show 0m while time remains */
          this.d.textContent = String(Math.floor(totalMin / 1440));
          this.h.textContent = String(Math.floor((totalMin % 1440) / 60)).padStart(2, '0');
          this.m.textContent = String(totalMin % 60).padStart(2, '0');
        }
      }
      /* next update on the next minute boundary; never every second */
      this.timer = setTimeout(this.tick, (left % 60000) + 50);
    }

    expire() {
      PX.track('offer_expired');
      this.remove();
    }
  }
  define('px-offer', PxOffer);

  /* == 04 DELIVERY ESTIMATE =============================================== */
  /* Business-day maths in the FULFILMENT time zone (data-tz), skipping
     weekends and data-holidays, honouring the dispatch cutoff. Calendar
     arithmetic runs on UTC dates so the shopper's own zone never shifts a
     day. Output: "Free delivery Thu, Oct 23 – Mon, Nov 3". */
  class PxDelivery extends HTMLElement {
    connectedCallback() {
      const out = qs('[data-px-delivery-text]', this);
      if (!out) return;
      try {
        const ds = this.dataset;
        const [cutH, cutM = 0] = (ds.cutoff || '14:00').split(':').map(Number);
        const hMin = Number(ds.handlingMin) || 0;
        const hMax = Number(ds.handlingMax) || 0;
        const tMin = Number(ds.transitMin) || 0;
        const tMax = Number(ds.transitMax) || 0;
        if (hMax + tMax === 0) return; /* no real data: keep the Liquid text */
        const holidays = new Set((ds.holidays || '').split(',').map((s) => s.trim()).filter(Boolean));

        const p = {};
        new Intl.DateTimeFormat('en-US', {
          timeZone: ds.tz || 'America/Chicago',
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).formatToParts(new Date()).forEach(({ type, value }) => { p[type] = Number(value); });

        const iso = (d) => d.toISOString().slice(0, 10);
        const isBiz = (d) => d.getUTCDay() !== 0 && d.getUTCDay() !== 6 && !holidays.has(iso(d));
        const addBiz = (d, n) => {
          const x = new Date(d.getTime());
          for (let c = 0; c < n;) { x.setUTCDate(x.getUTCDate() + 1); if (isBiz(x)) c += 1; }
          return x;
        };

        const today = new Date(Date.UTC(p.year, p.month - 1, p.day));
        const beforeCut = p.hour < cutH || (p.hour === cutH && p.minute < cutM);
        const received = beforeCut && isBiz(today) ? today : addBiz(today, 1);
        const first = addBiz(addBiz(received, hMin), tMin);
        const last = addBiz(addBiz(received, hMax), tMax);
        const fmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

        out.textContent = `${ds.lead || 'Delivery'} ${fmt.format(first)} – ${fmt.format(last)}`;
        this.setAttribute('data-px-ready', '');
      } catch (e) { /* keep the Liquid fallback text */ }
    }
  }
  define('px-delivery', PxDelivery);

  /* == 05 STICKY ADD-TO-CART ============================================== */
  class PxStickyAtc extends HTMLElement {
    connectedCallback() {
      this.btn = qs('[data-px-sticky-btn]', this);
      if (!document.getElementById(this.dataset.target) || !this.btn) return;

      /* Geometry, not IntersectionObserver: a jump scroll (anchor link, fast
         fling, "scroll to top") can carry the button from below the viewport
         to above it without ever intersecting, and IO then never fires.
         One rAF-throttled rect read per scroll frame is cheap. */
      const footer = qs('.shopify-section-group-footer-group') || qs('footer');
      let shownOnce = false;
      let ticking = false;

      const update = () => {
        ticking = false;
        const target = document.getElementById(this.dataset.target);
        if (!target) return;
        const passed = target.getBoundingClientRect().bottom < 0; /* scrolled PAST the real button */
        const footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        const show = passed && !footerIn;
        if (show === this.hasAttribute('data-visible')) return;
        this.toggleAttribute('data-visible', show);
        this.toggleAttribute('inert', !show);
        document.documentElement.classList.toggle('px-sticky-on', show);
        if (show && !shownOnce) { shownOnce = true; PX.track('sticky_shown'); }
      };
      this.request = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', this.request, { passive: true });
      window.addEventListener('resize', this.request, { passive: true });
      update();

      this.btn.addEventListener('click', () => {
        const real = document.getElementById(this.dataset.target);
        if (!real) return;
        PX.track('sticky_atc_click');
        if (real.disabled || real.getAttribute('aria-disabled') === 'true') {
          real.scrollIntoView({ behavior: behavior(), block: 'center' });
          return;
        }
        real.click(); /* one form, one checkout path */
      });

      this.unsub = PX.onVariantChange((evt) => {
        const fresh = evt?.data?.html?.querySelector('[data-px-sticky-price]');
        const price = qs('[data-px-sticky-price]', this);
        if (fresh && price) price.innerHTML = fresh.innerHTML;
        const v = evt?.data?.variant;
        if (v) {
          this.btn.disabled = !v.available;
          this.btn.textContent = v.available
            ? window.variantStrings?.addToCart || 'Add to cart'
            : window.variantStrings?.soldOut || 'Sold out';
          PX.config.variantId = v.id;
        }
      });
    }

    disconnectedCallback() {
      this.unsub?.();
      window.removeEventListener('scroll', this.request);
      window.removeEventListener('resize', this.request);
    }
  }
  define('px-sticky-atc', PxStickyAtc);

  /* == 06 FACT CHIPS -> ACCORDIONS / ANCHORS ============================== */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-px-open]');
    if (!link) return;
    const target = document.getElementById(link.dataset.pxOpen);
    if (!target) return; /* let the native href jump happen */
    e.preventDefault();
    if (target.tagName === 'DETAILS') target.open = true;
    target.scrollIntoView({ behavior: behavior(), block: 'start' });
    const focusEl = target.tagName === 'DETAILS' ? qs('summary', target) : target;
    if (focusEl === target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    focusEl?.focus({ preventScroll: true });
    PX.track('fact_open', { target: link.dataset.pxOpen });
  });

  /* == 07 ANALYTICS HOOKS ================================================= */
  (() => {
    if (!qs('.px-main')) return;

    PX.track('pdp_view', {
      has_offer: !!qs('px-offer'),
      has_reference_price: !!qs('.px-price__ref'),
      stock_state: qs('.px-stock--out') ? 'out' : qs('.px-stock--low') ? 'low' : 'in',
    });

    /* <details> 'toggle' does not bubble: listen in the capture phase */
    document.addEventListener('toggle', (e) => {
      const d = e.target;
      if (d instanceof HTMLDetailsElement && d.classList.contains('px-acc')) {
        PX.track('accordion_toggle', { id: d.id || null, open: d.open });
      }
    }, true);

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-px-atc]')) PX.track('atc_click', { source: 'main' });
      if (e.target.closest('[data-px-accel]')) PX.track('accelerated_checkout_click');
      if (e.target.closest('[data-px-installments]')) PX.track('installments_click');
      const help = e.target.closest('[data-px-help]');
      if (help) PX.track('help_click', { channel: help.dataset.pxHelp });
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        PX.track('section_view', { id: en.target.id || en.target.classList[0] });
        io.unobserve(en.target);
      }), { threshold: 0.25 });
      qsa('.pws-story, .cd-story, #px-why, #px-confidence, #px-about, #px-faq').forEach((el) => io.observe(el));
    }
  })();
})();
